

import * as Dialog from "@radix-ui/react-dialog";
import { useTheme } from "../Theme/ThemeProvider";
import React, { useRef, useState, useEffect } from "react";
import { StarIcon, StarFilledIcon } from "@radix-ui/react-icons";
import { submitFeedback } from "../../utils/feedback.service";
import { useMsal } from "@azure/msal-react";
import { useSelector } from 'react-redux';
import { RootState } from "../../store/store";


function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(val, max));
}

const AI_RESOURCE_HUB_URL = 'https://skydrive3m.sharepoint.com/teams/AI/SitePages/Prompt-Engineering.aspx?web=1';
const VIVA_ENGAGE_URL = 'https://engage.cloud.microsoft/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI0NTMyMzE3In0';

type SubmissionResult = {
  type: 'success' | 'error';
  detail?: string;
} | null;

function FeedbackSubmissionPopup({
  mode,
  result,
  onClose,
}: {
  mode: 'light' | 'dark';
  result: SubmissionResult;
  onClose: () => void;
}) {
  if (!result) {
    return null;
  }

  const isDark = mode === 'dark';
  const isSuccess = result.type === 'success';
  const popupTitle = isSuccess ? 'Thank You!' : 'Oops!';
  const popupMessage = isSuccess
    ? 'Your feedback helps make 3M Navigator better for everyone!'
    : 'Something went wrong. Please try again or visit';

  return (
    <Dialog.Root open={Boolean(result)} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[30500]" />
        <Dialog.Content
          className="fixed z-[31000] overflow-hidden rounded-2xl"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(calc(100vw - 2rem), 520px)',
            backgroundColor: isDark ? '#3a3a40' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15, 23, 42, 0.08)'}`,
            boxShadow: isDark
              ? '0 24px 60px rgba(0, 0, 0, 0.45)'
              : '0 24px 60px rgba(15, 23, 42, 0.18)',
          }}
        >
          <div
            style={{
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '20px',
              paddingBottom: '20px',
            }}
          >
            <Dialog.Title
              className="text-lg font-semibold"
              style={{
                color: isDark ? '#ffffff' : '#172b4d',
                fontFamily: '3MCircularTT',
              }}
            >
              {popupTitle}
            </Dialog.Title>

          </div>

          <div
            style={{
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15, 23, 42, 0.08)'}`,
            }}
          />

          <div
            style={{
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '14px',
              paddingBottom: '14px',
            }}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 shrink-0">
                {isSuccess ? (
                  <div
                    className="flex h-5 w-5 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: isDark ? '#22c55e' : '#16a34a',
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M9.5 3.5L5.1 7.9L2.9 5.7"
                        stroke="#3f3f46"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M11 2.5L20 18.5H2L11 2.5Z"
                      fill="#ef4444"
                    />
                    <path
                      d="M11 7.8V12.3"
                      stroke="#3f3f46"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <circle cx="11" cy="15.6" r="1" fill="#3f3f46" />
                  </svg>
                )}
              </div>

              <div
                className="text-sm leading-6"
                style={{
                  color: isDark ? 'rgba(255,255,255,0.9)' : '#374151',
                  fontFamily: '3MCircularTT',
                }}
              >
                {isSuccess ? (
                  <p>{popupMessage}</p>
                ) : (
                  <div style={{ lineHeight: 1.5 }}>
                    <p>Something went wrong. Please try again or visit</p>
                    <div style={{ marginTop: '2px' }}>
                      <a
                        href={AI_RESOURCE_HUB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: isDark ? '#9ecbff' : '#2563eb', textDecoration: 'underline' }}
                      >
                        AI Resource Hub
                      </a>{' '}
                      or{' '}
                      <a
                        href={VIVA_ENGAGE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: isDark ? '#9ecbff' : '#2563eb', textDecoration: 'underline' }}
                      >
                        Viva Engage
                      </a>{' '}
                      for more information.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="flex justify-end"
            style={{
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '14px',
              paddingBottom: '14px',
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15, 23, 42, 0.08)'}`,
            }}
          >
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md px-3 py-1.5 text-sm transition-opacity hover:opacity-80"
                style={{
                  color: isDark ? '#ffffff' : '#374151',
                  fontFamily: '3MCircularTT',
                }}
              >
                Close
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface FeedbackDialogProps {
    showFeedback: boolean;
    setShowFeedback: React.Dispatch<React.SetStateAction<boolean>>;
    fullScreen?: boolean;
}

/** Feedback dialog for user input and bug reports */
export function FeedbackDialog({ showFeedback, setShowFeedback, fullScreen = false }: FeedbackDialogProps) {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  
  // Form state
  const [feedbackType, setFeedbackType] = useState<'feedback' | 'bug'>('feedback');
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [description, setDescription] = useState('');
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>(null);

  // for feedback submission
  const { instance: msalInstance } = useMsal();
  const sessionId = useSelector((s: RootState) => s.chat.sessionId);


  useEffect(() => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setBoxSize({ width: rect.width, height: rect.height });
    }
  }, [mode]);

  // Reset form when dialog opens
  useEffect(() => {
    if (showFeedback) {
      setFeedbackType('feedback');
      setStarRating(0);
      setHoverRating(0);
      setDescription('');
      setSubmitError(null);
      setSubmissionResult(null);
    }
  }, [showFeedback]);

  /** Mouse event handlers for dragging the dialog */
  function onMouseDown(e: React.MouseEvent) {
    dragging.current = true;
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  function onMouseMove(e: MouseEvent) {
    if (dragging.current && boxRef.current) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newX = e.clientX - offset.current.x;
      let newY = e.clientY - offset.current.y;
      const minX = -(vw / 2) + boxSize.width / 2;
      const maxX = vw / 2 - boxSize.width / 2;
      const minY = -(vh / 2) + boxSize.height / 2;
      const maxY = vh / 2 - boxSize.height / 2;
      setPosition({
        x: clamp(newX, minX, maxX),
        y: clamp(newY, minY, maxY),
      });
    }
  }

  function onMouseUp() {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Validate rating (required, 1-5)
      if (starRating < 1 || starRating > 5) {
        setSubmitError('Please select a rating between 1 and 5 stars');
        setIsSubmitting(false);
        return;
      }

      await submitFeedback(msalInstance, {
        session: sessionId,
        rating: starRating,
        feedback: description.trim(),
      });
            
      // Close dialog and reset form on success
      setShowFeedback(false);
      setFeedbackType('feedback');
      setStarRating(0);
      setDescription('');
      setIsSubmitting(false);
      setSubmissionResult({ type: 'success' });
      
    } catch (error) {
      console.error('[FeedbackDialog] Failed to submit feedback:', error);
      setShowFeedback(false);
      setSubmissionResult({
        type: 'error',
        detail: error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.',
      });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowFeedback(false);
    // Reset form
    setFeedbackType('feedback');
    setStarRating(0);
    setDescription('');
    setSubmitError(null);
  };
  
  // For fullscreen mode on mobile
  if (fullScreen && showFeedback) {
    return (
      <>
        <div className="fixed inset-0 z-[30000] bg-[var(--color-bg)] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--color-text)]">
            <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
              We Value Your Input
            </h2>
            <button
              onClick={() => setShowFeedback(false)}
              className="p-2 rounded-full hover:bg-[var(--color-accent)]/10"
              style={{ color: 'var(--color-text)' }}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Critical Issues Notice */}
            <div className="mx-2 mb-6 p-3 rounded" style={{ 
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderLeft: '4px solid #3b82f6',
              fontFamily: '3MCircularTT',
              fontSize: '0.875rem'
            }}>
              <p style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '0.5rem' }}>
                Critical Issues
              </p>
              <p style={{ color: 'var(--color-text)', marginBottom: '0.5rem' }}>
                For any 3M Navigator-related issues, please submit a support request using:
              </p>
              <a 
                href="https://ithelp.mmm.com/Request.aspx?ID=3M_NAVIGATOR_SUPPORT_REQUEST" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
                style={{ 
                  fontFamily: '3MCircularTT',
                  wordBreak: 'break-all',
                  display: 'block',
                  marginBottom: '0.75rem'
                }}
              >
                ithelp.mmm.com/Request.aspx?ID=3M_NAVIGATOR_SUPPORT_REQUEST
              </a>
              <p style={{ color: 'var(--color-text)', marginTop: '0.75rem', marginBottom: '0.5rem' }}>
                We also encourage you to visit the Self-Service Portal for additional IT support resources, live chat with the Global Service Desk, and local contact numbers for urgent matters:
              </p>
              <a 
                href="https://apps.powerapps.com/play/e/default-facac3c4-e2a5-4257-af76-205c8a821ddb/a/72cac444-d543-4e57-b126-cec5ae4e88e5?tenantId=facac3c4-e2a5-4257-af76-205c8a821ddb&hint=4c4a9eaa-df60-4ac3-827e-f6e490aba727&sourcetime=1720643813831&source=portal&hidenavbar=true&source=teamsLinkUnfurling" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
                style={{ 
                  fontFamily: '3MCircularTT',
                  wordBreak: 'break-all',
                  display: 'block',
                  marginBottom: '0.75rem'
                }}
              >
                Self-Service Portal
              </a>
              <p style={{ color: 'var(--color-text)', marginBottom: '0.5rem' }}>
                For quick IT questions, we recommend using our direct chat support:
              </p>
              <a 
                href="https://itchatsupport.3m.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
                style={{ 
                  fontFamily: '3MCircularTT',
                  wordBreak: 'break-all'
                }}
              >
                itchatsupport.3m.com
              </a>
            </div>

            {/* Divider for mobile */}
            <div className="my-6 border-t border-[var(--color-text)]" />

            {/* Your Feedback Section */}
            <div className="mb-6">
              <h2 className="text-base font-semibold mb-4" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                Your Feedback
              </h2>
            </div>

            {/* Star Rating */}
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                How would you rate your overall experience?
              </h3>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => {
                      setHoverRating(star);
                      setStarRating(star);
                    }}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-colors duration-150 hover:scale-110"
                  >
                    {star <= (hoverRating || starRating) ? (
                      <StarFilledIcon className="w-8 h-8" style={{ color: '#fbbf24' }} />
                    ) : (
                      <StarIcon className="w-8 h-8" style={{ color: 'var(--color-text-muted)' }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                Tell us more (optional)
              </h3>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Share your thoughts..."
                rows={6}
                className="w-full p-3 rounded-lg border resize-none"
                style={{
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--color-text)',
                  borderColor: 'var(--color-accent)',
                  fontFamily: '3MCircularTT'
                }}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 rounded-lg border"
                style={{
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-text)',
                  fontFamily: '3MCircularTT'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                disabled={isSubmitting || starRating < 1 || !description.trim()}
                style={{
                  backgroundColor: 'var(--color-accent)',
                  color: 'white',
                  fontFamily: '3MCircularTT',
                  opacity: (isSubmitting || starRating < 1 || !description.trim()) ? 0.5 : 1,
                  cursor: (isSubmitting || starRating < 1 || !description.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>

        <FeedbackSubmissionPopup
          mode={mode}
          result={submissionResult}
          onClose={() => setSubmissionResult(null)}
        />
      </>
    );
  }
  
  return (
    <>
      <Dialog.Root open={showFeedback} onOpenChange={(open) => setShowFeedback(open)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[25000]" />
          <Dialog.Content
            ref={boxRef}
            className="fixed z-[30000] border border-[var(--color-accent)] rounded-lg p-6 shadow-xl"
            style={{
              backgroundColor: 'var(--color-feedback-bg)',
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`,
              maxWidth: 'min(90vw, 500px)',
              maxHeight: '90vh',
              width: '100%',
              overflowY: 'auto',
            }}
          >
          <Dialog.Title 
            className="text-xl font-semibold pb-3 border-b cursor-move" 
            style={{ 
              color: 'var(--color-text)', 
              fontFamily: '3MCircularTT',
              borderColor: 'var(--color-text)'
            }}
            onMouseDown={onMouseDown}
          >
            We Value Your Input
          </Dialog.Title>
          
          {/* Feedback Type Section */}
          {/* <div className="mt-6 mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
              Feedback type
            </h3>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="feedbackType"
                  value="feedback"
                  checked={feedbackType === 'feedback'}
                  onChange={(e) => setFeedbackType(e.target.value as 'feedback')}
                  className="mr-3"
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                <span style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>Feedback</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="feedbackType"
                  value="bug"
                  checked={feedbackType === 'bug'}
                  onChange={(e) => setFeedbackType(e.target.value as 'bug')}
                  className="mr-3"
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                <span style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>Report Bug</span>
              </label>
            </div>
          </div> */}

          {/* Critical Issues Notice */}
          <div className="mb-6">
            <h2 className="text-base font-semibold mt-4" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
              Critical Issues
            </h2>
          </div>

          <div className="mx-2 mb-6 mt-6 p-3 rounded" style={{ 
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderLeft: '4px solid #3b82f6',
            fontFamily: '3MCircularTT',
            fontSize: '0.875rem'
          }}>
            <p style={{ color: 'var(--color-text)', marginBottom: '0.5rem' }}>
              For any 3M Navigator-related issues, please submit a support request using:
            </p>
            <a 
              href="https://ithelp.mmm.com/Request.aspx?ID=3M_NAVIGATOR_SUPPORT_REQUEST" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              style={{ 
                fontFamily: '3MCircularTT',
                wordBreak: 'break-all',
                display: 'block',
                marginBottom: '0.75rem'
              }}
            >
              ithelp.mmm.com/Request.aspx?ID=3M_NAVIGATOR_SUPPORT_REQUEST
            </a>
            <p style={{ color: 'var(--color-text)', marginTop: '0.75rem', marginBottom: '0.5rem' }}>
              We also encourage you to visit the Self-Service Portal for additional IT support resources, live chat with the Global Service Desk, and local contact numbers for urgent matters:
            </p>
            <a 
              href="https://apps.powerapps.com/play/e/default-facac3c4-e2a5-4257-af76-205c8a821ddb/a/72cac444-d543-4e57-b126-cec5ae4e88e5?tenantId=facac3c4-e2a5-4257-af76-205c8a821ddb&hint=4c4a9eaa-df60-4ac3-827e-f6e490aba727&sourcetime=1720643813831&source=portal&hidenavbar=true&source=teamsLinkUnfurling" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              style={{ 
                fontFamily: '3MCircularTT',
                wordBreak: 'break-all',
                display: 'block',
                marginBottom: '0.75rem'
              }}
            >
              Self-Service Portal
            </a>
            <p style={{ color: 'var(--color-text)', marginBottom: '0.5rem' }}>
              For quick IT questions, we recommend using our direct chat support:
            </p>
            <a 
              href="https://itchatsupport.3m.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              style={{ 
                fontFamily: '3MCircularTT',
                wordBreak: 'break-all'
              }}
            >
              itchatsupport.3m.com
            </a>
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-[var(--color-text)]" />

          {/* Your Feedback Section */}
          <div className="mb-6">
            <h2 className="text-base font-semibold mb-4" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
              General Feedback & Suggestions
            </h2>
          </div>

          {/* Star Rating for desktop for feedback */}
          {/* {feedbackType === 'feedback' && ( */}
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                How would you rate your overall experience?
              </h3>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => {
                      setHoverRating(star);
                      setStarRating(star); // Set rating on hover
                    }}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-colors duration-150 hover:scale-110"
                  >
                    {star <= (hoverRating || starRating) ? (
                      <StarFilledIcon
                        className="w-8 h-8"
                        style={{ color: '#fbbf24' }}
                      />
                    ) : (
                      <StarIcon
                        className="w-8 h-8"
                        style={{ color: 'var(--color-text)' }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          {/* )} */}

          {/* File Upload - Only for Report Bug */}
          {/* {feedbackType === 'bug' && (
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                A screenshot will help us understand your issue better
              </h3>
              <div className="border-2 border-dashed rounded-lg p-4 text-center transition-colors hover:border-[var(--color-accent)]"
                style={{ borderColor: 'var(--color-accent)' }}>
                <input
                  type="file"
                  id="file-upload"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <UploadIcon className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
                  <span className="text-sm" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
                    {uploadedFile ? uploadedFile.name : 'Click to upload screenshot'}
                  </span>
                </label>
              </div>
            </div>
          )} */}

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)', fontFamily: '3MCircularTT' }}>
              Description <span style={{ color: '#ef4444' }}>*</span>
            </h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={feedbackType === 'feedback' ? 'Tell us about your experience...' : 'Describe the bug you encountered...'}
              rows={4}
              required
              className="w-full p-3 rounded border resize-none"
              style={{
                backgroundColor: 'var(--color-secondary)',
                borderColor: 'var(--color-accent)',
                color: 'var(--color-text)',
                fontFamily: '3MCircularTT'
              }}
            />
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="mb-4 p-3 rounded" style={{ 
              backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
              borderLeft: '4px solid #ef4444',
              color: isDark ? '#fca5a5' : '#ef4444',
              fontFamily: '3MCircularTT',
              fontSize: '0.875rem'
            }}>
              {submitError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="py-2 rounded transition"
              style={{ 
                fontFamily: '3MCircularTT',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--color-text)',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.color = 'var(--color-text)';
                }
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!description.trim() || isSubmitting || starRating < 1}
              className="px-6 py-2 rounded border border-[var(--color-accent-hover)] hover:bg-gray-500 hover:text-[var(--color-bg)] bg-[var(--button-background-color)] text-[var(--color-text)] transition disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <FeedbackSubmissionPopup
        mode={mode}
        result={submissionResult}
        onClose={() => setSubmissionResult(null)}
      />
    </>
  );
}